// Serviço de Agentes/Times/Categorias contra o WootDash — substitui o uso de
// fetchAgentsFull/fetchAgentReport/fetchAllTeamsWithMembers (Chatwoot) nesta view.
// Modelo é intencionalmente diferente do Chatwoot: Team é ligado a Unit+UF, Category
// é uma lista própria (não um enum fixo aprendizagem/comercial/financeiro/r&s), e
// atribuição agente↔time / agente↔categoria é feita pelo lado do agente
// (/agentes/{id}/times, /agentes/{id}/categorias) — não existe endpoint de "membros
// do time". Isso é a arquitetura do WootDash; a tela se adapta a ela.
import { WOOTDASH_BASE, wootdashAuthHeaders } from '@/api/wootdash'

export interface CpAgent {
  id: string
  firstName: string
  lastName: string
  email: string
  phone: string | null
  status: string
  isActive: boolean
  maxConcurrentChats: number
  avatarUrl: string | null
  /** Ramal no PABX (Linkus). */
  extension?: string | null
  // RBAC real (Estágio 1 da correção do Dashboard) — substitui o hack local
  // useAdmin().profileLabel (chaveado por chatwootId, admin_config em localStorage).
  roleNames: string[]
  roleIds: string[]
  /** Modo observador: só acompanha — não recebe conversa nem responde o contato. */
  isObserver?: boolean
  /** Meu perfil › Cabeçalho das mensagens: o nome vai no cabeçalho do bot, e se nome e sobrenome. */
  showNameInHeader?: boolean
  headerFullName?: boolean
}

export interface CpTeam {
  id: string
  unitId: string
  name: string
  uf: string | null
  isFallback: boolean
}

export interface CpCategory {
  id: string
  name: string
}

async function get<T>(token: string, path: string, fallback: T): Promise<T> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}${path}`, { headers: wootdashAuthHeaders(token) })
    if (!res.ok) return fallback
    return await res.json()
  } catch {
    return fallback
  }
}

async function mutate(token: string, method: string, path: string, body?: unknown): Promise<{ ok: boolean; status: number; data?: any }> {
  const res = await fetch(`${WOOTDASH_BASE}${path}`, {
    method,
    headers: { ...wootdashAuthHeaders(token), ...(body ? { 'Content-Type': 'application/json' } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  })
  const data = res.status !== 204 ? await res.json().catch(() => undefined) : undefined
  return { ok: res.ok, status: res.status, data }
}

export function listAgents(token: string): Promise<{ agents: CpAgent[]; total: number }> {
  return get(token, '/agentes', { agents: [], total: 0 })
}

export async function createAgent(token: string, data: {
  firstName: string; lastName: string; email: string; password: string; phone?: string; maxConcurrentChats?: number
}) {
  return mutate(token, 'POST', '/agentes', data)
}

/** Liga/desliga o modo observador. 400 (RoleNotAllowed) pra quem tem perfil Agente ou Supervisor. */
export async function setAgentObserver(token: string, id: string, observador: boolean) {
  return mutate(token, 'PATCH', `/agentes/${id}/observador`, { observador })
}

export async function updateAgent(token: string, id: string, data: { firstName: string; lastName: string; phone?: string; maxConcurrentChats: number }) {
  return mutate(token, 'PUT', `/agentes/${id}`, data)
}

export async function deactivateAgent(token: string, id: string) {
  return mutate(token, 'DELETE', `/agentes/${id}`)
}

export async function changeAgentPassword(token: string, id: string, currentPassword: string, newPassword: string) {
  return mutate(token, 'PATCH', `/agentes/${id}/senha`, { currentPassword, newPassword })
}

export async function updateAgentAvatar(token: string, id: string, avatarUrl: string) {
  return mutate(token, 'PATCH', `/agentes/${id}/avatar`, { avatarUrl })
}

export type AgentAvailability = 'Offline' | 'Online' | 'Busy'

// Self-serviço — sempre a própria conta (via claim do token, não um id de rota), ver
// PATCH /agentes/eu/status. Login já marca Online sozinho; ficar Offline/Ocupado é
// escolha do agente.
export async function updateOwnStatus(token: string, status: AgentAvailability) {
  return mutate(token, 'PATCH', '/agentes/eu/status', { status })
}

/** Ramal do próprio agente (Meu perfil). Vazio tira. Erro 400/409 traz o motivo em data.reason. */
export async function updateOwnExtension(token: string, extension: string | null) {
  return mutate(token, 'PATCH', '/agentes/eu/ramal', { extension })
}

export function getAgent(token: string, id: string): Promise<CpAgent | null> {
  return get<CpAgent | null>(token, `/agentes/${id}`, null)
}

export function listTeams(token: string): Promise<CpTeam[]> {
  return get(token, '/times', [])
}

export function listCategories(token: string): Promise<CpCategory[]> {
  return get(token, '/categorias', [])
}

export interface CpAgentTeamLink { id: string; name: string }

/** Times do agente logado (GET /agentes/eu/times, sem teams.view). chatwootId só vem pra time importado. */
export interface MyTeam { id: string; name: string; chatwootId: number | null }

export async function listMyTeams(token: string): Promise<MyTeam[]> {
  const rows = await get<{ teamId: string; teamName: string; chatwootId: number | null }[]>(token, '/agentes/eu/times', [])
  return rows.map(r => ({ id: r.teamId, name: r.teamName, chatwootId: r.chatwootId ?? null }))
}

// GET /agentes/{id}/times devolve {teamId, teamName} (visão resumida de atribuição,
// não o TeamSummary completo) — normaliza pro mesmo formato usado na tela.
export async function listAgentTeams(token: string, agentId: string): Promise<CpAgentTeamLink[]> {
  const rows = await get<{ teamId: string; teamName: string }[]>(token, `/agentes/${agentId}/times`, [])
  return rows.map(r => ({ id: r.teamId, name: r.teamName }))
}

export async function assignAgentToTeam(token: string, agentId: string, teamId: string) {
  return mutate(token, 'POST', `/agentes/${agentId}/times/${teamId}`)
}

export async function revokeAgentFromTeam(token: string, agentId: string, teamId: string) {
  return mutate(token, 'DELETE', `/agentes/${agentId}/times/${teamId}`)
}

// GET /agentes/{id}/categorias devolve {categoryId, categoryName} — mesma normalização.
export async function listAgentCategories(token: string, agentId: string): Promise<CpCategory[]> {
  const rows = await get<{ categoryId: string; categoryName: string }[]>(token, `/agentes/${agentId}/categorias`, [])
  return rows.map(r => ({ id: r.categoryId, name: r.categoryName }))
}

export async function assignCategoryToAgent(token: string, agentId: string, categoryId: string) {
  return mutate(token, 'POST', `/agentes/${agentId}/categorias/${categoryId}`)
}

export async function revokeCategoryFromAgent(token: string, agentId: string, categoryId: string) {
  return mutate(token, 'DELETE', `/agentes/${agentId}/categorias/${categoryId}`)
}

// RBAC — atribuir/revogar papel (perfil) de um agente. Permissão admin.createAgent,
// mesma da criação de agente (WootDash.Api/Rbac/RoleEndpoints.cs).
export async function assignAgentRole(token: string, agentId: string, roleId: string) {
  return mutate(token, 'POST', `/agentes/${agentId}/papeis/${roleId}`)
}

export async function revokeAgentRole(token: string, agentId: string, roleId: string) {
  return mutate(token, 'DELETE', `/agentes/${agentId}/papeis/${roleId}`)
}

/** Meu perfil › Cabeçalho das mensagens: o nome no cabeçalho do bot quando eu respondo (e se nome e sobrenome). */
export async function updateOwnHeaderName(token: string, mostrarNome: boolean, nomeCompleto: boolean) {
  return mutate(token, 'PATCH', '/agentes/eu/cabecalho', { mostrarNome, nomeCompleto })
}
